const GROUP_ORDER = [
  "classification",
  "pricing",
  "property",
  "location",
  "sources",
  "media",
  "quality",
  "identifiers",
  "timestamps",
];

const PRICING_KEYS = new Set([
  "price",
  "priceMin",
  "priceMax",
  "currency",
  "administrationFee",
  "rentPeriod",
]);

const PROPERTY_KEYS = new Set([
  "bedrooms",
  "bathrooms",
  "parkingSpaces",
  "area",
  "areaM2",
  "constructedAreaM2",
  "privateAreaM2",
  "lotAreaM2",
  "builtAreaM2",
  "yearBuilt",
  "estrato",
  "amenities",
  "features",
]);

const LOCATION_KEYS = new Set([
  "address",
  "addressText",
  "neighborhood",
  "municipality",
  "city",
  "department",
  "country",
  "latitude",
  "longitude",
  "location",
  "locationQuality",
  "coordinateSource",
  "coordinateConfidence",
  "claimedNeighborhood",
  "computedNeighborhood",
  "neighborhoodMatchStatus",
]);

const SOURCE_KEYS = new Set([
  "source",
  "primarySourceKey",
  "sourceUrl",
  "sourceCount",
  "activeSourceCount",
  "sourceSummary",
  "broker",
  "attribution",
  "contactContext",
  "trustContext",
  "qualityScore",
  "matchConfidenceScore",
]);

const MEDIA_KEYS = new Set([
  "visibleImageCount",
  "hiddenImageCount",
  "healthyImageCount",
  "brokenImageCount",
  "hasImages",
  "primaryImageAttribution",
  "primaryImageIsCached",
  "primaryImageHealthStatus",
  "manualPrimaryGalleryImageId",
  "galleryLastUpdatedAt",
  "imageLastUpdatedAt",
  "mediaQuality",
]);

const QUALITY_KEYS = new Set(["freshness", "dataQuality"]);

const CLASSIFICATION_KEYS = new Set([
  "listingType",
  "propertyType",
  "lifecycleStatus",
  "availabilityStatus",
  "canonicalStatus",
  "status",
  "reviewStatus",
  "createdFrom",
]);

export function isIdentifierAttributeKey(key) {
  const normalized = String(key || "");
  if (!normalized) return false;
  if (normalized === "id" || normalized === "canonicalKey") return true;
  if (/uuid|guid/i.test(normalized)) return true;
  if (/Id$/.test(normalized) || normalized.endsWith("ID")) return true;
  return false;
}

export function isTimestampAttributeKey(key) {
  const normalized = String(key || "");
  if (!normalized) return false;
  if (TIMESTAMP_KEYS.has(normalized)) return true;
  return /(?:At|Date)$/.test(normalized);
}

const TIMESTAMP_KEYS = new Set(["createdAt", "lastSeenAt", "boundaryAssignedAt"]);

export function classifyListingAttributeKey(key) {
  const normalized = String(key || "");
  if (!normalized) return "classification";
  if (isIdentifierAttributeKey(normalized)) return "identifiers";
  if (isTimestampAttributeKey(normalized)) return "timestamps";
  if (PRICING_KEYS.has(normalized)) return "pricing";
  if (PROPERTY_KEYS.has(normalized)) return "property";
  if (LOCATION_KEYS.has(normalized)) return "location";
  if (SOURCE_KEYS.has(normalized)) return "sources";
  if (MEDIA_KEYS.has(normalized)) return "media";
  if (QUALITY_KEYS.has(normalized)) return "quality";
  if (CLASSIFICATION_KEYS.has(normalized)) return "classification";
  return "classification";
}

export function groupListingAttributes(attributesBySection = {}) {
  const merged = new Map();

  for (const rows of Object.values(attributesBySection)) {
    for (const row of rows || []) {
      if (!row?.key || merged.has(row.key)) continue;
      merged.set(row.key, row.value);
    }
  }

  const buckets = Object.fromEntries(GROUP_ORDER.map((id) => [id, []]));

  for (const [key, value] of merged) {
    buckets[classifyListingAttributeKey(key)].push({ key, value });
  }

  buckets.identifiers.sort((a, b) => a.key.localeCompare(b.key));
  buckets.timestamps.sort((a, b) => a.key.localeCompare(b.key));

  return GROUP_ORDER.map((id) => ({ id, rows: buckets[id] })).filter((group) => group.rows.length > 0);
}

export { GROUP_ORDER as LISTING_ATTRIBUTE_GROUP_ORDER };
